import { config } from 'js-xdr';
import { BundleData as StorefrontBundleData, CDNBundleSettings } from '../types';

export function toLineItemProperty(bundle: { variantId: string; version: number; items: any[] }) {
  const opts = {
    variantId: xdr.Uint64.fromString(bundle.variantId.toString()),
    version: bundle.version || Math.floor(Date.now() / 1000),
    items: bundle.items.map(item => {
      return new xdr.BundleItem({
        collectionId: xdr.Uint64.fromString(item.collectionId.toString()),
        productId: xdr.Uint64.fromString(item.productId.toString()),
        variantId: xdr.Uint64.fromString(item.variantId.toString()),
        sku: item.sku || '',
        quantity: item.quantity,
        ext: new xdr.BundleItemExt(0),
      });
    }),
    ext: new xdr.BundleExt(0),
  };

  const xdrBundle = new xdr.Bundle(opts);
  return xdr.BundleEnvelope.envelopeTypeBundle(xdrBundle).toXDR('base64');
}

const xdr = config(
  (xdr: {
    enum: (arg0: string, arg1: { envelopeTypeBundle: number }) => void;
    typedef: (arg0: string, arg1: any) => void;
    uint: () => any;
    uhyper: () => any;
    union: (
      arg0: string,
      arg1: {
        switchOn: any;
        switchName: string;
        switches: any[][] | string[][];
        // eslint-disable-next-line @typescript-eslint/ban-types
        arms: {} | {} | { v1: any };
      }
    ) => void;
    int: () => any;
    void: () => any;
    struct: (arg0: string, arg1: any[][]) => void;
    lookup: (arg0: string) => any;
    string: () => any;
    varArray: (arg0: any, arg1: number) => any;
  }) => {
    xdr.enum('EnvelopeType', {
      envelopeTypeBundle: 0,
    });
    xdr.typedef('Uint32', xdr.uint());
    xdr.typedef('Uint64', xdr.uhyper());
    xdr.union('BundleItemExt', {
      switchOn: xdr.int(),
      switchName: 'v',
      switches: [[0, xdr.void()]],
      arms: {},
    });
    xdr.struct('BundleItem', [
      ['collectionId', xdr.lookup('Uint64')],
      ['productId', xdr.lookup('Uint64')],
      ['variantId', xdr.lookup('Uint64')],
      ['sku', xdr.string()],
      ['quantity', xdr.lookup('Uint32')],
      ['ext', xdr.lookup('BundleItemExt')],
    ]);
    xdr.union('BundleExt', {
      switchOn: xdr.int(),
      switchName: 'v',
      switches: [[0, xdr.void()]],
      arms: {},
    });
    xdr.struct('Bundle', [
      ['variantId', xdr.lookup('Uint64')],
      ['items', xdr.varArray(xdr.lookup('BundleItem'), 500)],
      ['version', xdr.lookup('Uint32')],
      ['ext', xdr.lookup('BundleExt')],
    ]);
    xdr.union('BundleEnvelope', {
      switchOn: xdr.lookup('EnvelopeType'),
      switchName: 'type',
      switches: [['envelopeTypeBundle', 'v1']],
      arms: {
        v1: xdr.lookup('Bundle'),
      },
    });
  }
);

export interface BundleSelectionItems {
  collectionId: string;
  externalProductId: string;
  externalVariantId: string;
  quantity: number;
}

interface VariantSettings {
  externalVariantId: string;
  optionSources: Record<string, { collectionId: string; min: number | null; max: number | null }>;
  ranges: { min: number; max: number | null }[];
}

interface BundleSettings {
  bundleSettings: { maxPerItem: number | null; isCustomizable: boolean };
  variantsSettings: Record<string, VariantSettings>;
}

type BundleDataCollection = StorefrontBundleData['collections'][string];

interface BundleData extends Omit<StorefrontBundleData, 'collections'> {
  collections: Record<
    string,
    Omit<BundleDataCollection, 'products'> & {
      products: Record<string, BundleDataCollection['products'][number]>;
    }
  >;
}

export class BundleSelectionValidator {
  #bundleSettings: BundleSettings | null;
  #items: BundleSelectionItems[];
  #bundleData: BundleData | null;

  constructor(selectionItems: BundleSelectionItems[], bundleData: StorefrontBundleData) {
    this.#bundleSettings = this.#formatBundleSettings(bundleData);
    this.#bundleData = this.#formatBundleData(bundleData);
    this.#items = selectionItems;
  }

  #formatBundleSettings(bundleData: StorefrontBundleData) {
    try {
      const bundleSettings = bundleData.bundle_settings;
      return {
        bundleSettings: {
          maxPerItem: bundleSettings.max_quantity_per_variant,
          isCustomizable: bundleSettings.is_customizable,
        },
        variantsSettings: bundleSettings.variants.reduce<BundleSettings['variantsSettings']>((acc, variant) => {
          if (!variant.enabled) return acc;

          let ranges: VariantSettings['ranges'] = [{ min: variant.items_count, max: variant.items_count }];
          if (!variant.items_count) {
            ranges = variant.ranges.map(({ quantity_max, quantity_min }) => ({
              min: quantity_min,
              max: quantity_max,
            }));
          }

          acc[variant.external_variant_id] = {
            externalVariantId: variant.external_variant_id,
            optionSources: variant.option_sources.reduce<VariantSettings['optionSources']>((acc, optionSource) => {
              acc[optionSource.option_source_id] = {
                min: optionSource.quantity_min,
                max: optionSource.quantity_max,
                collectionId: optionSource.option_source_id,
              };
              return acc;
            }, {}),
            ranges,
          };
          return acc;
        }, {}),
      };
    } catch (error) {
      return null;
    }
  }

  #formatBundleData(bundleData: StorefrontBundleData) {
    try {
      const collections = Object.values(bundleData.collections).reduce<BundleData['collections']>((acc, current) => {
        acc[current.id] = {
          ...current,
          products: current.products.reduce<BundleData['collections'][string]['products']>((productAcc, product) => {
            productAcc[product.id] = product;
            return productAcc;
          }, {}),
        };

        return acc;
      }, {});

      return { ...bundleData, collections };
    } catch (error) {
      return null;
    }
  }

  #isValueInRange(ranges: { min: number | null; max: number | null }[], value: number) {
    const matchedRanges = ranges.filter(({ min, max }) => {
      const minValue = min ?? value;
      const maxValue = max ?? value;

      return minValue <= value && value <= maxValue;
    });

    return !!matchedRanges.length;
  }

  #unique(ids: string[]) {
    return Array.from(new Set(ids));
  }

  isInitialDataValid() {
    if (!this.#bundleData) {
      throw 'Bundle data does not exist for the given product.';
    }

    if (!this.#bundleSettings) {
      throw 'Bundle settings do not exist for the given product.';
    }

    if (!Array.isArray(this.#items) || !this.#items.length) {
      throw 'No bundle selection items provided.';
    }

    return true;
  }

  isValidVariant(externalVariantId: string) {
    if (!this.#bundleSettings?.variantsSettings[externalVariantId]) {
      throw `The ${externalVariantId} bundle variant is invalid.`;
    }

    return true;
  }

  isSelectionInVariantRange(externalVariantId: string) {
    const total = this.#items.reduce((acc, current) => acc + current.quantity, 0);
    const ranges = this.#bundleSettings?.variantsSettings[externalVariantId].ranges || [];

    if (!this.#isValueInRange(ranges, total)) {
      throw 'The total number of products does not fall within the required variant range.';
    }

    return true;
  }

  areItemsInMaxPerItemBound() {
    const maxPerItem = this.#bundleSettings?.bundleSettings.maxPerItem || 0;
    const invalidProducts = this.#items.filter(({ quantity }) => quantity > maxPerItem);

    if (maxPerItem && invalidProducts.length) {
      const invalidIds = this.#unique(invalidProducts.map(({ externalProductId }) => externalProductId)).join(', ');
      throw `The selected products (${invalidIds}) exceed the maximum limit of ${maxPerItem} items per product.`;
    }

    return true;
  }

  areSelectedCollectionsValid(externalVariantId: string) {
    const variantSettings = this.#bundleSettings?.variantsSettings[externalVariantId];
    const invalidCollections = this.#items.filter(({ collectionId }) => !variantSettings?.optionSources[collectionId]);
    if (invalidCollections.length) {
      const invalidIds = this.#unique(invalidCollections.map(({ collectionId }) => collectionId)).join(', ');
      throw `The collections (${invalidIds}) are no longer valid for the bundle variant (${externalVariantId}).`;
    }

    return true;
  }

  areItemsQuantitiesValidForTheCollection(externalVariantId: string) {
    const totalSelectedByCollection = this.#items.reduce<Record<string, number>>((acc, current) => {
      acc[current.collectionId] = current.quantity + (acc[current.collectionId] || 0);
      return acc;
    }, {});

    const variantSettings = this.#bundleSettings?.variantsSettings[externalVariantId];
    const invalidItems = Object.values(variantSettings?.optionSources || {}).filter(collection => {
      const quantity = totalSelectedByCollection[collection.collectionId] || 0;
      return !this.#isValueInRange([collection], quantity);
    });

    if (!variantSettings || invalidItems.length) {
      const invalidIds = this.#unique(invalidItems.map(({ collectionId }) => collectionId)).join(', ');
      throw `The selection exceeds the limits for the collections (${invalidIds}).`;
    }

    return true;
  }

  areSelectedProductsValid(externalVariantId: string) {
    const bundleVariantCollections = this.#bundleSettings?.variantsSettings[externalVariantId]?.optionSources || {};
    const variantCollections = Object.values(bundleVariantCollections).reduce<BundleData['collections']>(
      (acc, optionSource) => {
        const collection = this.#bundleData?.collections[optionSource.collectionId];
        if (collection) {
          acc[optionSource.collectionId] = collection;
        }
        return acc;
      },
      {}
    );

    if (!Object.values(variantCollections).length) {
      throw `No collections found for the bundle variant (${externalVariantId}).`;
    }

    const missingProducts = this.#items.filter(
      ({ externalProductId, collectionId }) => !variantCollections[collectionId]?.products[externalProductId]
    );

    if (missingProducts.length) {
      const invalidIds = this.#unique(missingProducts.map(({ externalProductId }) => externalProductId)).join(', ');
      throw `The products (${invalidIds}) are no longer valid for the bundle product.`;
    }

    return true;
  }

  isBundleSelectionValid(externalVariantId: string) {
    this.isInitialDataValid();
    this.isValidVariant(externalVariantId);
    this.isSelectionInVariantRange(externalVariantId);
    this.areItemsInMaxPerItemBound();
    this.areSelectedCollectionsValid(externalVariantId);
    this.areItemsQuantitiesValidForTheCollection(externalVariantId);
    this.areSelectedProductsValid(externalVariantId);

    return true;
  }
}
